import LOADING_STATUSES from '../constants/loading-statuses';
import PropTypes from 'prop-types';
import React from 'react';
import { useIsFetching } from '@llt-frontend/react-fetch';

//

const LoadingContext = React.createContext();

//

function LoadingContextProvider({ children, initialStatus = LOADING_STATUSES.idle }) {
  const isFetching = useIsFetching();

  const queue = React.useRef(0);
  const [status, setStatus] = React.useState(isFetching || initialStatus);

  //
  // Methods

  const setLoadingStatus = React.useCallback((status) => {
    queue.current = Math.max(0, queue.current + (status === LOADING_STATUSES.pending ? 1 : -1));
    setStatus(queue.current > 0 ? LOADING_STATUSES.pending : status);
  }, []);

  /**
   * Wraps a block of code within an async loading status indicator.
   * This function should be awaited.
   */
  const withLoadingStatus = React.useCallback(
    async (
      /** The code to wrap around a loading status. */
      callback,
      /** Optional cleanup code to always run at the end, even after failure. */
      finallyCallback
    ) => {
      try {
        setLoadingStatus(LOADING_STATUSES.pending);
        const result = await callback();
        setLoadingStatus(LOADING_STATUSES.complete);
        return result;
      } catch (ex) {
        setLoadingStatus(LOADING_STATUSES.failed);
        throw ex;
      } finally {
        finallyCallback?.();
      }
    },
    [setLoadingStatus]
  );

  //
  // Render

  return (
    <LoadingContext.Provider
      value={{
        get loading() {
          return status === LOADING_STATUSES.pending || Boolean(isFetching);
        },
        get loadingStatus() {
          return status;
        },
        setLoadingStatus,
        withLoadingStatus,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
}

LoadingContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
};

// Exports
// -------

export { LoadingContextProvider, LoadingContext };

export default LoadingContext;
